import type { Channel } from './get-channels-from-pypes';
import type { PlatformConnection } from './get-pype-connections';
import { toChannelEnv } from './map-connection-to-row';
import { isAgentProPlatform } from './platform-catalog';

/**
 * AgentPro is Pypestream's own legacy agent console, no longer a supported Live
 * Agent Platform (PSTUDIO-4344). A connection to it is matched on EITHER field
 * it carries: `agentRouting` is the backend enum value (`pypestream`), and
 * `platformId` is the key the connection records for itself. Display only — the
 * stored connection is never changed.
 */
export const isAgentProConnection = (
  connection: Pick<PlatformConnection, 'agentRouting' | 'platformId'>
): boolean =>
  isAgentProPlatform(connection.agentRouting) ||
  isAgentProPlatform(connection.platformId);

/** The connections Pro Studio shows: everything except AgentPro. */
export const excludeAgentProConnections = (
  connections: PlatformConnection[] | null | undefined
): PlatformConnection[] =>
  (connections ?? []).filter((connection) => !isAgentProConnection(connection));

export type UnconnectedStatus = 'none' | 'agentpro';

export interface UnconnectedChannel {
  channel: Channel;
  /** `none`: never connected. `agentpro`: it was bound to AgentPro. */
  status: UnconnectedStatus;
  /**
   * AgentPro connections this channel's pype is still bound to in this env.
   * Connecting the channel detaches them first, so it is never left attached
   * to two connections.
   */
  agentProConnectionIds: string[];
}

/**
 * The one place that decides "not connected to a Live Agent Platform" for the
 * connection-based surfaces: a channel with no supported connection in its env,
 * or only an AgentPro one. (Default Messages decides the same thing from the
 * pype's own routing, through `hasLiveAgentPlatform`.)
 *
 * `channels` are the env's channels as `getChannelsFromPypes` derives them.
 * A pype can back several channels and the binding is per pype, so connecting
 * one connects all of them.
 */
export const findUnconnectedChannels = (
  connections: PlatformConnection[] | null | undefined,
  channels: Channel[],
  env: 'live' | 'sandbox'
): UnconnectedChannel[] => {
  const supportedPypes = new Set<string>();
  const agentProByPype = new Map<string, string[]>();

  for (const connection of connections ?? []) {
    const agentPro = isAgentProConnection(connection);

    for (const pype of connection.pypes) {
      if (toChannelEnv(pype.env) !== env) continue;

      if (agentPro) {
        agentProByPype.set(pype.pypeId, [
          ...(agentProByPype.get(pype.pypeId) ?? []),
          connection.id,
        ]);
      } else {
        supportedPypes.add(pype.pypeId);
      }
    }
  }

  return channels
    .filter(
      (channel) => channel.env === env && !supportedPypes.has(channel.pypeId)
    )
    .map((channel): UnconnectedChannel => {
      const agentProConnectionIds = agentProByPype.get(channel.pypeId) ?? [];

      return {
        channel,
        status:
          agentProConnectionIds.length > 0 ||
          isAgentProPlatform(channel.agentRouting)
            ? 'agentpro'
            : 'none',
        agentProConnectionIds,
      };
    })
    .sort((a, b) =>
      a.channel.name.localeCompare(b.channel.name, undefined, {
        sensitivity: 'base',
      })
    );
};
